import { useSelector } from 'react-redux';

import {
    AppState, selectCurrentProductId
} from '../app';
import {
    APP_CONFIG,
    AppConfigurationKeys,
    ConfigurationsData,
    DucsInitConfig,
    LoginURLConfigJSON
} from '../config';
import { ConfigurationState } from './configuration.types';
import { assert } from '@test-helpers';
import {
    isDefined,
    minutesToSeconds,
    tryParseFloat
} from '@utils';

function selectConfigurationKey (
    key: AppConfigurationKeys,
    state: AppState,
    fallback: null | any = null
) {
    const configValue = state.configuration.data?.[key];

    if (!isDefined(configValue)) {
        if (!fallback) {
            console.error('useSelectorConfigValue() Config key', key, 'was not found and there is no fallback value');
        }

        return fallback;
    }

    return configValue;
}

export const useSelectorConfigurationState = () => {
    return useSelector<AppState, ConfigurationState>(
        state => state.configuration
    );
};

export const useSelectorConfigValue = <T extends keyof ConfigurationsData>(key: T, fallback?: string) => {
    return useSelector<AppState, ConfigurationsData[T]>(state => {
        return selectConfigurationKey(key, state, fallback);
    });
};

export function selectTrySigninUrl (state: AppState): string {
    const currentProductId = selectCurrentProductId(state);
    const loginURLConfig = selectLoginUrlConfig(state);
    const signinUrl = loginURLConfig.find(
        config => config.productId === currentProductId
    )?.signinUrl;

    assert(typeof signinUrl === 'string', 'Unable to determine signinUrl from configuration');

    return signinUrl;
}

export function selectLoginUrlConfig (state: AppState): LoginURLConfigJSON {
    const loginURLConfig: LoginURLConfigJSON = selectConfigurationKey('LoginURL', state, []);

    return loginURLConfig;
}

export function selectPromoVideoUrl (state: AppState) {
    const promoVideoConfig = selectConfigurationKey('PromoVideoUrl', state, '{}');

    return JSON.parse(promoVideoConfig);
}

export function useSelectorPromoVideoUrl () {
    return useSelector(selectPromoVideoUrl);
}

export function useSelectorHasConfiguration () {
    return useSelector<AppState, boolean>(state => {
        return state.configuration.data !== null;
    });
}

export function selectIdleSession (state: AppState) {
    const sessionExpiryTimeInMinutes = selectConfigurationKey(
        'sessionExpiryTimeInMinutes', state, 'sessionExpiryTimeInMinutes'
    );
    const sessionOverrideTimeInMinutes = selectConfigurationKey(
        'sessionOverrideTimeInMinutes', state, 'sessionOverrideTimeInMinutes'
    );
    const sessionBufferTimeInSeconds = selectConfigurationKey(
        'sessionBufferTimeInSeconds', state, 'sessionBufferTimeInSeconds'
    );
    const fallback = APP_CONFIG.defaultCountdownTimeoutInMins;
    const countdownTimeoutInMinutes = selectConfigurationKey(
        'countdownTimeoutInMinutes', state, fallback
    );

    return {
        sessionExpiryTimeInMinutes: tryParseFloat(sessionExpiryTimeInMinutes) || 60,
        sessionOverrideTimeInMinutes: tryParseFloat(sessionOverrideTimeInMinutes) || 60,
        countdownTimeoutInMinutes: tryParseFloat(countdownTimeoutInMinutes) || APP_CONFIG.defaultCountdownTimeoutInMins,
        sessionBufferTimeInSeconds: tryParseFloat(sessionBufferTimeInSeconds) || 10
    };
}

export function useSelectorCountdownTimeoutSecs () {
    return useSelector(selectCountdownTimeoutSecs);
}

export function selectCountdownTimeoutSecs (state: AppState): number {
    const fallback = APP_CONFIG.defaultCountdownTimeoutInMins;
    const configValue = selectConfigurationKey(
        'countdownTimeoutInMinutes', state, fallback
    );
    // For testing allow a value lower than a minute.
    const value = tryParseFloat(configValue);

    if (!value) {
        return minutesToSeconds(fallback);
    }

    return minutesToSeconds(value);
}

export function useSelectorNeedHelp () {
    return useSelector(selectNeedHelpUrl);
}

export function selectNeedHelpUrl (state: AppState): string {
    const configValue = selectConfigurationKey(
        'NeedHelpUrl', state, 'NeedHelpUrl'
    );

    return configValue;
}

export function useSelectorPromoVideoValidTeacherYearGroups () {
    return useSelector(selectPromoVideoValidTeacherYearGroups);
}

export function selectPromoVideoValidTeacherYearGroups (state: AppState): number[] {
    const configValue = selectConfigurationKey(
        'promoVideoValidTeacherYearGroups', state, '[]'
    );

    return JSON.parse(configValue);
}

export function useSelectorPromoVideoCountryCodes () {
    return useSelector(selectPromoVideoCountryCodes);
}

export function selectPromoVideoCountryCodes (state: AppState): string[] {
    const configValue = selectConfigurationKey(
        'promoVideoCountryCodes', state, '[]'
    );

    return JSON.parse(configValue);
}

export function useSelectorShowPromoVideoModal () {
    return useSelector(selectShowPromoVideoModal);
}

export function selectShowPromoVideoModal (state: AppState): string {
    const configValue = selectConfigurationKey(
        'showPromoVideoModal', state, 'showPromoVideoModal'
    );

    return configValue;
}

export function useSelectorAssignAndReviewPromoVideoUrl () {
    return useSelector(selectAssignAndReviewPromoVideoUrl);
}

export function selectAssignAndReviewPromoVideoUrl (state: AppState): string {
    const configValue = selectConfigurationKey(
        'AssignReviewPromoVideoUrl', state, 'AssignReviewPromoVideoUrl'
    );

    return configValue;
}

export function useSelectorNewCoursesPromoVideoUrl () {
    return useSelector(selectNewCoursesPromoVideoUrl);
}

export function selectNewCoursesPromoVideoUrl (state: AppState): string {
    const configValue = selectConfigurationKey(
        'NewCoursesPromoVideoUrl', state, 'NewCoursesPromoVideoUrl'
    );

    return configValue;
}

export function useSelectorFeedbackUrl () {
    return useSelector(selectFeedbackUrl);
}

export function selectFeedbackUrl (state: AppState): string {
    const configValue = selectConfigurationKey(
        'FeedbackUrl', state, 'FeedbackUrl'
    );

    return configValue;
}

export function useSelectorTryNewCourseModuleIds () {
    return useSelector(selectTryNewCourseModuleIds);
}

export function selectTryNewCourseModuleIds (state: AppState): number[] {
    const configValue = selectConfigurationKey(
        'tryNewCourseModuleIds', state, '[]'
    );

    return JSON.parse(configValue);
}

export function useSelectorCombinedAssignModuleId () {
    return useSelector(selectCombinedAssignModuleId);
}

export function selectCombinedAssignModuleId (state: AppState): number {
    const configValue = selectConfigurationKey(
        'COMBINED_ASSIGN_MODULE_ID', state, ''
    );

    return parseInt(configValue, 10);
}

export function useSelectorNumeracyForEarlyLearnersModuleId () {
    return useSelector(selectNumeracyForEarlyLearnersModuleId);
}

export function selectNumeracyForEarlyLearnersModuleId (state: AppState): number {
    const configValue = selectConfigurationKey(
        'numeracyForEarlyLearnersModuleId', state, '0'
    );

    return parseInt(configValue, 10);
}

export function useSelectorModuleIdsForNewBadgeStyling () {
    return useSelector(selectModuleIdsForNewBadgeStyling);
}

export function selectModuleIdsForNewBadgeStyling (state: AppState): string {
    const configValue = selectConfigurationKey(
        'ModuleIdsForNewBadgeStyling', state, '0'
    );

    return configValue;
}

export function useSelectorThreeEssentialsUrl () {
    return useSelector(selectThreeEssentialsUrl);
}

export function selectThreeEssentialsUrl (state: AppState): string {
    const configValue = selectConfigurationKey(
        'THREE_ESSENTIALS_URL', state, 'threeEssentialsUrl'
    );

    return configValue;
}

export function selectThreeEssentialsLogoutQueryParamKey (state: AppState) {
    return selectConfigurationKey('THREE_ESSENTIALS_LOGOUT_QUERY_PARAM_KEY', state, 'logout_return_to');
}

export function useSelectorPromoBannerConfig () {
    return useSelector((state: AppState) => {
        const promoConfig = selectConfigurationKey('PromoBanner', state, {});

        if (Object.keys(promoConfig).length === 0) return null;

        return JSON.parse(promoConfig);
    });
}

export function useSelectorReadingUrl () {
    return useSelector(selectReadingUrl);
}

export function selectReadingUrl (state: AppState): string {
    const configValue = selectConfigurationKey(
        'READING_URL', state, 'readingUrl'
    );

    return configValue;
}

export function useSelectorWritingUrl () {
    return useSelector(selectWritingUrl);
}

export function selectWritingUrl (state: AppState): string {
    const configValue = selectConfigurationKey(
        'WRITING_URL', state, 'writingUrl'
    );

    return configValue;
}

export function useSelectorMathematicsUrl () {
    return useSelector(selectMathematicsUrl);
}

export function selectMathematicsUrl (state: AppState): string {
    const configValue = selectConfigurationKey(
        'MATHEMATICS_URL', state, 'mathematicsUrl'
    );

    return configValue;
}

export function useSelectorEditMyProfileUrl () {
    return useSelector(selectEditMyProfileUrl);
}

export function selectEditMyProfileUrl (state: AppState): string {
    const configValue = selectConfigurationKey(
        'THREE_ESSENTIALS_EDIT_MY_PROFILE_URL', state, ''
    );

    return configValue;
}

export function useSelectorSignupForFreeTrialUrl () {
    return useSelector(selectorSignupForFreeTrialUrl);
}

export function selectorSignupForFreeTrialUrl (state: AppState): string {
    const configValue = selectConfigurationKey(
        'SignupForFreeTrial', state, ''
    );

    return configValue;
}

export function useSelectorRolloverModuleId () {
    return useSelector(selectorRolloverModuleId);
}

export function selectorRolloverModuleId (state: AppState): string {
    const configValue = selectConfigurationKey(
        'rolloverModuleId', state, '0'
    );

    return configValue;
}

export function useSelectorMxAPACCountryCodes () {
    return useSelector(selectorMxAPACCountryCodes);
}

export function selectorMxAPACCountryCodes (state: AppState): string {
    const configValue = selectConfigurationKey(
        'mxAPACCountryCodes', state, []
    );

    return configValue;
}

export function useSelectorDucsInitConfig (): DucsInitConfig {
    const DUCSLibraryUrl = useSelectorConfigValue('DUCSLibraryUrl');
    const DUCSCdnUrl = useSelectorConfigValue('DUCSCdnUrl');
    const DUCSContentApiEndpoint = useSelectorConfigValue('DUCSContentApiEndpoint');
    const DUCSAuthenticationGatewayEndpoint = useSelectorConfigValue('DUCSAuthenticationGatewayEndpoint');

    assert(typeof DUCSLibraryUrl === 'string', 'DUCSLibraryUrl is required');
    assert(typeof DUCSContentApiEndpoint === 'string', 'DUCSContentApiEndpoint is required');
    assert(typeof DUCSCdnUrl === 'string', 'DUCSCdnUrl is required');
    assert(typeof DUCSAuthenticationGatewayEndpoint === 'string', 'DUCSAuthenticationGatewayEndpoint is required');

    return {
        DUCSLibraryUrl,
        DUCSCdnUrl,
        DUCSContentApiEndpoint,
        DUCSAuthenticationGatewayEndpoint
    };
}

export function useSelectEnv () {
    const env = useSelectorConfigValue('env');

    assert(typeof env === 'string', 'env is required');

    return env;
}

/*
While you're testing, set tracesSampleRate to 1.0, as that ensures that every transaction will be sent to Sentry.
Once testing is complete, we recommend lowering this value in production by either lowering your tracesSampleRate value,
or switching to using tracesSampler to dynamically sample and filter your transactions.
https://docs.sentry.io/platforms/javascript/performance/
 */
export function useSelectTraceSampleRate () {
    const traceSampleRate = useSelectorConfigValue('TracesSampleRate');

    // We expect defined config value can be parsed correctly.
    return traceSampleRate ? parseFloat(traceSampleRate) : 1;
}

